import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '../../../store';
import { WORKFLOW_STALENESS_THRESHOLD } from '../constants';

/**
 * Extend DynamicFeatureState to include encounter toggle and hints state
 */
declare module '@store/types' {
  interface DynamicFeatureState {
    'encounters/toggle'?: {
      consecutiveV2Notes: number;
    };
    'encounters/hints'?: {
      iosRecordingTipDismissed: boolean;
    };
  }
}

/**
 * Base selector for workflow state
 * Note: Reducer key is 'encounters/workflow' (string with slash), not nested encounters.workflow
 */
export const selectWorkflowState = (state: RootState) => (state as any)['encounters/workflow'];

/**
 * Select active workflow encounter ID
 */
export const selectActiveWorkflowEncounterId = createSelector(
  [selectWorkflowState],
  workflow => workflow?.activeWorkflowEncounterId ?? null,
);

/**
 * Select last accessed timestamp
 */
export const selectLastAccessedAt = createSelector(
  [selectWorkflowState],
  workflow => workflow?.lastAccessedAt ?? null,
);

/**
 * Check if workflow is stale (>1 hour inactive)
 */
export const selectIsWorkflowStale = createSelector([selectLastAccessedAt], lastAccessedAt => {
  if (!lastAccessedAt) return false;
  return Date.now() - lastAccessedAt > WORKFLOW_STALENESS_THRESHOLD;
});

/**
 * Check if user has an active workflow
 */
export const selectHasActiveWorkflow = createSelector(
  [selectActiveWorkflowEncounterId],
  id => id !== null,
);

/**
 * Selector for encounter toggle state
 * Returns the consecutive V2 note count for graduation tracking
 */
export const selectEncounterToggleState = (state: RootState): number => {
  return state['encounters/toggle']?.consecutiveV2Notes ?? 0;
};

/**
 * Selector for iOS recording tip dismissal
 */
export const selectIosRecordingTipDismissed = (state: RootState): boolean => {
  return state['encounters/hints']?.iosRecordingTipDismissed ?? false;
};
